import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { divergenceReportsQueryOptions } from "@/server/divergence";
import { DashboardLayout } from "@/components/dashboard-layout";
import { DivergenceReportsTable } from "./-components/divergences-table";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

export type DivergenceReport = {
  id: number;
  username: string;
  userAgent: string;
  service: string;
  callId: string;
  caller: string;
  created: string;
  reason: string;
  message: string;
};

type DivergenceSearch = {
  divergenceId?: number;
  search?: string;
  after?: string;
  before?: string;
  page?: number;
  pageSize?: number;
};

export const Route = createFileRoute("/_dashboard/divergences")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): DivergenceSearch => {
    return {
      divergenceId: (search.divergenceId as number) || undefined,
      search: (search.search as string) || undefined,
      after: (search.after as string) || undefined,
      before: (search.before as string) || undefined,
      page: search.page ? Number(search.page) : undefined,
      pageSize: search.pageSize ? Number(search.pageSize) : undefined,
    };
  },
  loader: () => {
    return {
      crumb: "Divergences",
    };
  },
});

function RouteComponent() {
  const { divergenceId, search, after: from, before: to, page, pageSize } = Route.useSearch();
  const navigate = Route.useNavigate();

  const defaultRange = useMemo(() => {
    const thirtyDaysInMilliseconds = 1000 * 60 * 60 * 24 * 30;

    const toDate = new Date(new Date().getTime() + 1000 * 60 * 60 * 24);

    const fromDate = new Date(toDate.getTime() - thirtyDaysInMilliseconds);

    const returnValue = {
      before: toDate,
      after: fromDate,
    };
    return returnValue;
  }, []);

  const [after, setAfter] = useState(from || defaultRange.after.toISOString());
  const [before, setBefore] = useState(to || defaultRange.before.toISOString());

  const divergenceQuery = useQuery(
    divergenceReportsQueryOptions({
      after: after || defaultRange.after.toISOString(),
      before: before || defaultRange.before.toISOString(),
    })
  );
  const divergenceReports: DivergenceReport[] = (divergenceQuery.data || [])
    .filter((item) => item.id != null && item.id >= 0)
    .map((item) => ({
      id: item.id!,
      username: item.username ?? "",
      userAgent: item.userAgent ?? "",
      service: item.service ?? "",
      callId: item.callId ?? "",
      caller: item.caller ?? "",
      created: item.created ? new Date(item.created * 1000).toISOString() : "",
      reason: item.reason ?? "",
      message: item.truncatedMessage ?? "",
    }));

  useEffect(() => {
    if (divergenceQuery.isError) {
      const message =
        divergenceQuery.error instanceof Error
          ? divergenceQuery.error.message
          : "Failed to load divergence reports. Please try again later.";
      toast.error(message);
    }
  }, [divergenceQuery.isError, divergenceQuery.error]);

  useEffect(() => {
    if (from) {
      setAfter(from);
    }
  }, [from]);

  useEffect(() => {
    if (to) {
      setBefore(to);
    }
  }, [to]);

  useEffect(() => {
    if (after && before) {
      navigate({
        search: {
          divergenceId: divergenceId || undefined,
          search: search || undefined,
          after: after,
          before: before,
          page: page || undefined,
          pageSize: pageSize || undefined,
        },
      });
    }
  }, [after, before, divergenceId, search, page, pageSize, navigate]);

  return (
    <DashboardLayout submenu={null}>
      <DivergenceReportsTable
        divergenceReports={divergenceReports}
        isLoading={divergenceQuery.isLoading}
        defaultDivergenceId={divergenceId}
        defaultSearch={search}
        before={before}
        after={after}
        setAfter={setAfter}
        setBefore={setBefore}
        defaultPage={page}
        defaultPageSize={pageSize}
        onNavigate={navigate}
      />
    </DashboardLayout>
  );
}

export default RouteComponent;
